import { lazy, Suspense, useState, type ComponentType, type LazyExoticComponent } from "react";
import { BuildNotice } from "@/ui/BuildNotice";
import { ErrorBoundary } from "@/ui/ErrorBoundary";
import { legacyGameRedirect, NOT_FOUND_ROUTE, resolveRoute, ROUTES, type Route } from "./routes";

/** One lazy component per chunk, made once (routes sharing a chunk share the component). */
const PAGES = new Map<Route["chunk"], LazyExoticComponent<ComponentType>>();
for (const r of [...ROUTES, NOT_FOUND_ROUTE])
  if (!PAGES.has(r.chunk)) PAGES.set(r.chunk, lazy(r.chunk));

/** Old game deep links (`/?debug=1`) move to `/play?debug=1` without a reload. */
function initialRoute(): Route {
  const { pathname, search, hash } = window.location;
  const to = legacyGameRedirect(pathname, search, hash);
  if (to) window.history.replaceState(window.history.state, "", to);
  return resolveRoute(window.location.pathname);
}

const ELEMENTS = new Map([...PAGES].map(([chunk, Page]) => [chunk, <Page key="page" />]));

/**
 * The shell: picks the chunk for this path (routes.ts) and lazy-loads it. The page is chosen once
 * per document load; moving between chunks is a normal navigation (the site moves between its own
 * pages without one).
 */
export function App() {
  const [route] = useState(initialRoute);
  return (
    <ErrorBoundary>
      <Suspense fallback={<div className={`boot-overlay boot-${route.id}`} aria-busy="true" />}>
        {ELEMENTS.get(route.chunk)}
      </Suspense>
      <BuildNotice />
    </ErrorBoundary>
  );
}
